import apiClient from './client'

import type { Centro, CentroInput, PaginatedResponse } from '@/types/gia'

const LIST_ALL_CACHE_TTL_MS = 60 * 1000
let listAllCache: { data: Centro[]; expiresAt: number } | null = null
let listAllInFlight: Promise<Centro[]> | null = null

const clearListAllCache = () => {
  listAllCache = null
}

const centrosApi = {
  list: async (page = 1): Promise<PaginatedResponse<Centro>> => {
    const response = await apiClient.get<PaginatedResponse<Centro>>('/centros/', {
      params: { page }
    })

    return response.data
  },

  listAll: async (): Promise<Centro[]> => {
    const now = Date.now()

    if (listAllCache && listAllCache.expiresAt > now) {
      return listAllCache.data
    }

    if (listAllInFlight) {
      return listAllInFlight
    }

    listAllInFlight = (async () => {
      const centros: Centro[] = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const response = await apiClient.get<PaginatedResponse<Centro> | Centro[]>('/centros/', {
          params: { page }
        })

        const data = response.data

        if (Array.isArray(data)) {
          centros.push(...data)
          hasMore = false
        } else {
          if (Array.isArray(data.results)) {
            centros.push(...data.results)
          }

          hasMore = Boolean(data.next)
        }

        page += 1
      }

      listAllCache = {
        data: centros,
        expiresAt: Date.now() + LIST_ALL_CACHE_TTL_MS
      }

      return centros
    })()

    try {
      return await listAllInFlight
    } finally {
      listAllInFlight = null
    }
  },

  create: async (data: CentroInput): Promise<Centro> => {
    const response = await apiClient.post<Centro>('/centros/', data)

    clearListAllCache()

    return response.data
  },

  update: async (id: number, data: Partial<CentroInput>): Promise<Centro> => {
    const response = await apiClient.put<Centro>(`/centros/${id}/`, data)

    clearListAllCache()

    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/centros/${id}/`)

    clearListAllCache()
  }
}

export default centrosApi
